import { useState, useMemo } from 'react';
import { CardSummary } from '../../types/cards';
import { validateDeck } from '../../api/decks';
import { DECK_RULES } from '../../types/decks';
import { Card } from '../inventory/Card';
import './DeckBuilder.css';

interface DeckBuilderProps {
  cards: CardSummary[];
  selectedCardIds: number[];
  onCardSelect: (cardId: number) => void;
  onCardRemove: (cardId: number) => void;
  onClear: () => void;
  deckName: string;
  onDeckNameChange: (name: string) => void;
}

export function DeckBuilder({
  cards,
  selectedCardIds,
  onCardSelect,
  onCardRemove,
  onClear,
  deckName,
  onDeckNameChange,
}: DeckBuilderProps) {
  const [filterGrade, setFilterGrade] = useState<string>('all');
  const [filterAttribute, setFilterAttribute] = useState<string>('all');

  // 선택된 카드 정보
  const selectedCards = useMemo(() => {
    const cardMap = new Map(cards.map((card) => [card.id, card]));
    const cardCountMap = new Map<number, number>();

    selectedCardIds.forEach((id) => {
      cardCountMap.set(id, (cardCountMap.get(id) || 0) + 1);
    });

    return Array.from(cardCountMap.entries())
      .map(([id, count]) => {
        const card = cardMap.get(id);
        return card ? { ...card, selectedCount: count } : null;
      })
      .filter((card): card is NonNullable<typeof card> => card !== null);
  }, [selectedCardIds, cards]);

  // 필터링된 카드 목록
  const filteredCards = useMemo(() => {
    return cards.filter((card) => {
      if (filterGrade !== 'all' && card.grade !== filterGrade) return false;
      if (filterAttribute !== 'all' && card.attribute !== filterAttribute) return false;
      return true;
    });
  }, [cards, filterGrade, filterAttribute]);

  // 카드별 선택 개수
  const cardCountMap = useMemo(() => {
    const map = new Map<number, number>();
    selectedCardIds.forEach((id) => {
      map.set(id, (map.get(id) || 0) + 1);
    });
    return map;
  }, [selectedCardIds]);

  // 덱 검증
  const validation = useMemo(() => {
    return validateDeck(selectedCardIds, cards);
  }, [selectedCardIds, cards]);

  const handleCardClick = (card: CardSummary) => {
    const currentCount = cardCountMap.get(card.id) || 0;
    const userCount = card.count;

    if (currentCount < Math.min(userCount, DECK_RULES.MAX_DUPLICATES)) {
      onCardSelect(card.id);
    }
  };

  const handleRemoveCard = (cardId: number) => {
    onCardRemove(cardId);
  };

  return (
    <div className="deck-builder">
      {/* 덱 정보 헤더 */}
      <div className="deck-builder__header">
        <input
          type="text"
          className="deck-builder__name-input"
          placeholder="덱 이름을 입력하세요"
          value={deckName}
          onChange={(e) => onDeckNameChange(e.target.value)}
          maxLength={30}
        />
        <div className="deck-builder__stats">
          <span className="deck-builder__count">
            {selectedCardIds.length} / {DECK_RULES.MAX_CARDS}
          </span>
          {validation.valid ? (
            <span className="deck-builder__status deck-builder__status--valid">✓ 준비됨</span>
          ) : (
            <span className="deck-builder__status deck-builder__status--invalid">⚠ 검증 필요</span>
          )}
        </div>
      </div>

      {/* 검증 오류 메시지 */}
      {validation.errors.length > 0 && (
        <div className="deck-builder__errors">
          {validation.errors.map((error, index) => (
            <div key={index} className="deck-builder__error">
              {error}
            </div>
          ))}
        </div>
      )}

      {/* 선택된 카드 목록 */}
      {selectedCards.length > 0 && (
        <div className="deck-builder__selected">
          <div className="deck-builder__selected-header">
            <h3>선택된 카드 ({selectedCards.length}종)</h3>
            <button className="deck-builder__clear-btn" onClick={onClear}>
              모두 제거
            </button>
          </div>
          <div className="deck-builder__selected-grid">
            {selectedCards.map((card) => (
              <div key={card.id} className="deck-builder__selected-item">
                <Card card={card} />
                <div className="deck-builder__selected-count">
                  {card.selectedCount} / {card.count}
                </div>
                <button
                  className="deck-builder__remove-btn"
                  onClick={() => handleRemoveCard(card.id)}
                  aria-label="제거"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 필터 */}
      <div className="deck-builder__filters">
        <select
          className="deck-builder__filter"
          value={filterGrade}
          onChange={(e) => setFilterGrade(e.target.value)}
        >
          <option value="all">모든 등급</option>
          <option value="SS">SS</option>
          <option value="S">S</option>
          <option value="A">A</option>
          <option value="B">B</option>
          <option value="C">C</option>
          <option value="D">D</option>
        </select>
        <select
          className="deck-builder__filter"
          value={filterAttribute}
          onChange={(e) => setFilterAttribute(e.target.value)}
        >
          <option value="all">모든 속성</option>
          <option value="sand">🏜️ 모래</option>
          <option value="sun">☀️ 태양</option>
          <option value="wind">💨 바람</option>
          <option value="oasis">💧 오아시스</option>
          <option value="sandstorm">🌪️ 모래폭풍</option>
          <option value="star">⭐ 별</option>
          <option value="ruins">🗿 유적</option>
        </select>
      </div>

      {/* 카드 목록 */}
      <div className="deck-builder__cards">
        {filteredCards.map((card) => {
          const selectedCount = cardCountMap.get(card.id) || 0;
          const canAdd = selectedCount < Math.min(card.count, DECK_RULES.MAX_DUPLICATES);
          const isMaxed = selectedCount >= DECK_RULES.MAX_DUPLICATES;

          return (
            <div
              key={card.id}
              className={`deck-builder__card-item ${!canAdd ? 'deck-builder__card-item--disabled' : ''} ${selectedCount > 0 ? 'deck-builder__card-item--selected' : ''}`}
              onClick={() => canAdd && handleCardClick(card)}
            >
              <Card card={card} />
              <div className="deck-builder__card-info">
                {selectedCount > 0 && (
                  <div className="deck-builder__card-count">
                    {selectedCount} / {Math.min(card.count, DECK_RULES.MAX_DUPLICATES)}
                  </div>
                )}
                {isMaxed && <div className="deck-builder__card-max">최대</div>}
                {!canAdd && card.count === 0 && (
                  <div className="deck-builder__card-unavailable">보유 없음</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

